/**
 * Overview-tab TanStack Query hooks (Plan 06 Task 6.3).
 *
 * Each hook unwraps the server envelope and returns the inner payload.
 * `queryKey` starts with `['overview', <endpoint>, <stripped-filters>]`
 * so the TanStack cache is keyed by the semantic filter set (not the raw
 * URL), keeping cache hits across equivalent links.
 *
 * `staleTime: 60_000` matches the Overview tab's expected cadence: users
 * stay on the tab tweaking filters for a minute before new data lands.
 * `useSyncStatus` (Plan 05) does its own 60s poll — no polling here.
 */

import { useQuery } from '@tanstack/react-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import {
  getActivityTrend,
  getOverviewKpis,
  getRatingDistribution,
  getRecentFeedback,
} from '@/services/api-client';
import type {
  ActivityTrendPoint,
  FeedbackRow,
  OverviewKpis,
  RatingBucket,
} from '@/services/api-types';

const STALE_TIME = 60_000;

export function useOverviewKpis(filters: Filters) {
  return useQuery<OverviewKpis>({
    queryKey: ['overview', 'kpis', filtersQuestion(filters)],
    queryFn: async () => (await getOverviewKpis(filters)).data,
    staleTime: STALE_TIME,
  });
}

export function useActivityTrend(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useQuery<ActivityTrendPoint[]>({
    queryKey: [
      'v2',
      'overview',
      'activity-trend',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getActivityTrend(filters, granularity)).data,
    staleTime: STALE_TIME,
  });
}

export function useRatingDistribution(filters: Filters) {
  return useQuery<RatingBucket[]>({
    queryKey: [
      'v2',
      'overview',
      'rating-distribution',
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getRatingDistribution(filters)).data,
    staleTime: STALE_TIME,
  });
}

export function useRecentFeedback(filters: Filters, limit = 5) {
  return useQuery<FeedbackRow[]>({
    queryKey: [
      'v2',
      'overview',
      'recent-feedback',
      limit,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getRecentFeedback(filters, limit)).data,
    staleTime: STALE_TIME,
  });
}
